// @flow
import { ADD_ITEM, REM_ITEM } from '../actions/diff.actions';
import { DIMENSIONS_UPDATED, DIMENSIONS_UPDATING } from '../actions/project.actions';

const DATAPOINT_GROUPS = [
  {
    type: 'lighthouse',
    name: 'Lighthouse',
    datapoints: ['lh_failed_report', 'lh_score']
  },

  {
    type: 'meta',
    name: 'Meta',
    description: 'All relevant Meta Tags , such as Canonical URLs, robots.txt, and hreflang tags',
    datapoints: ['canonical', 'hrflang', 'protocol', 'robots']
  },

  {
    type: 'content',
    name: 'Content',
    description: 'Content-related data such as the # of words on site, # of H1 and H2 tags, etc.',
    datapoints: ['h1', 'h2', 'links', 'words']
  },

  {
    type: 'titledesc',
    name: 'Titles and Descriptions',
    description:
      'Title, and title length in # of letters and description and description length in # of letters.',
    datapoints: ['description', 'description_length', 'title_length']
  },

  {
    type: 'structdata',
    name: 'Structured Data',
    description:
      'A list of all structured data  of Schema.org and whether OpenGraph from Facebook is correctly implemented.',
    datapoints: ['structured_data', 'og_valid']
  },

  {
    type: 'images',
    name: 'Images',
    description:
      'Data points relating to images on your site such as # of images and # of images with ALT tags.',
    datapoints: ['images', 'alt']
  },

  {
    type: 'user_settings',
    name: 'User Settings',
    description:
      'Data points related to settings of the user browsing your site such whether he uses an ad blocker, etc.',
    datapoints: ['ad_blocker', 'tab_history', 'private_browsing']
  }
];

const DATAPOINTS = [
  { name: 'Number of H1', description: 'Number of <h1> tags on the page', sid: 'h1' },
  { name: 'Number of H2', description: 'Number of <h2> tags on the page', sid: 'h2' },
  { name: 'Description', description: 'Page meta description content', sid: 'description' },
  {
    name: 'Description Length',
    description: 'Page meta description content Length',
    sid: 'description_length'
  },
  { name: 'Images with alt', description: 'Number of img tags with alt on the page', sid: 'alt' },
  { name: 'Canonical Url', description: 'Canonical url of the page', sid: 'canonical' },
  { name: 'Hreflang', description: 'Hreflang on the page', sid: 'hrflang', type: 'meta' },
  { name: 'Number of Images', description: 'Number of <img> tags on the page', sid: 'images' },
  { name: 'Number of Links', description: 'Number of links on the page', sid: 'links' },
  { name: 'Is OpenGraph Valid', description: 'Is Open Graph valid on the page', sid: 'og_valid' },
  { name: 'Protocol', description: 'Is it Http/Https', sid: 'protocol' },
  { name: 'Robots', description: 'Robots meta content', sid: 'robots' },
  { name: 'Title Length', description: 'Title content length', sid: 'title_length' },
  { name: 'Number of Words', description: 'Number of words on the page', sid: 'words' },
  {
    name: 'Tab History',
    description: 'How many Pages the User Visited in the Tab-Browser Sessions',
    sid: 'tab_history'
  },
  {
    name: 'Ad Blocker',
    description: 'Detect if a User is using an Adblocker',
    sid: 'ad_blocker',
    scope: 'SESSION'
  },
  {
    name: 'Private Browsing',
    description: 'Detect if a User is in incognito mode',
    sid: 'private_browsing',
    scope: 'SESSION'
  },
  {
    name: 'Structured Data',
    description: 'Get valid structured data JSON-LD, Microdata, RDFa',
    sid: 'structured_data'
  },

  {
    name: 'Lighthouse Score',
    description: 'Lighthouse generic score',
    sid: 'lh_score'
  },

  {
    name: 'Lighthouse Failed Report',
    description: 'Lighthouse failed report',
    sid: 'lh_failed_report'
  }
  //{ name: 'Page Size, Kb', description: 'The size of a page in kilobytes', sid: 'page_size' }
];

const DATAPOINTS_NAMES = DATAPOINTS.map((d) => d.name);

const initialState = {
  available: DATAPOINTS,
  groups: DATAPOINT_GROUPS,
  accountDatapoints: [],
  baselineDatapoints: null,

  toadd: DATAPOINTS,
  added: [],
  baseline: null,

  updating: false,
  isChanged: false
};

export default (state: Object = initialState, action: { type: String, data: Object }) => {
  let newState = { ...state };
  let newAccountDatapoints;
  let activityArray;
  let idx;
  switch (action.type) {
    case ADD_ITEM:
      newAccountDatapoints = [...state.accountDatapoints];
      action.data.active = true;
      action.data.isTouched = true;

      activityArray = newAccountDatapoints.map((p) => p.active);
      idx = activityArray.indexOf(false);
      if (idx !== -1) {
        newAccountDatapoints[idx] = { ...newAccountDatapoints[idx], ...action.data };
      } else {
        newAccountDatapoints.push(action.data);
      }
      newAccountDatapoints = newAccountDatapoints.map(flagOPHDatapoint);
      return {
        ...state,
        accountDatapoints: newAccountDatapoints,
        updating: false,
        available: newAccountDatapoints.reduce(ApplyDiffToDatapoints.bind(this), DATAPOINTS),
        isChanged: state.baseline !== JSON.stringify(newAccountDatapoints)
      };

    case REM_ITEM:
      if (!action.data.isAccountDatapoint) {
        newAccountDatapoints = state.accountDatapoints.filter((p) => p.name !== action.data.name);
      } else {
        newAccountDatapoints = state.accountDatapoints.map((p) => ({
          ...p,
          active: p.name !== action.data.name ? p.active : false
        }));
      }
      return {
        ...state,
        accountDatapoints: newAccountDatapoints,
        updating: false,
        available: newAccountDatapoints.reduce(ApplyDiffToDatapoints.bind(this), DATAPOINTS),
        isChanged: state.baseline !== JSON.stringify(newAccountDatapoints)
      };
    case DIMENSIONS_UPDATED:
      return {
        ...state,
        accountDatapoints: action.data.map(flagOPHDatapoint).map(flagAccountDatapoints),
        baseline: JSON.stringify(action.data),
        updating: false,
        available: action.data.reduce(ApplyDiffToDatapoints.bind(this), DATAPOINTS)
      };

    case DIMENSIONS_UPDATING:
      newState.updating = true;
      newState.added = [];
      newState.toadd = newState.added.reduce(ApplyDiffToDatapoints.bind(this), DATAPOINTS);

      return {
        ...state,
        accountDatapoints: [],
        baseline: null,
        updating: true,
        available: [...DATAPOINTS]
      };
    default:
      return {
        ...state
      };
  }
};

function flagAccountDatapoints(point) {
  return { ...point, isAccountDatapoint: true };
}
function flagOPHDatapoint(point) {
  const idx = DATAPOINTS.map((p) => p.name).indexOf(point.name);
  return {
    ...point,
    isOPHDatapoint: !!~idx,
    description: !!~idx ? DATAPOINTS[idx].description : undefined,
    sid: !!~idx ? DATAPOINTS[idx].sid : undefined
  };
}
function ApplyDiffToDatapoints(res, action) {
  return res.filter((r) => !action.active || r.name !== action.name);
}



// WEBPACK FOOTER //
// ./src/reducers/datapointsReducer.js